import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  Stack,
  Title,
} from "@mantine/core";
import { FunctionComponent, useEffect, useState } from "react";
import { useAuth } from "reactfire";
import {
  createReview,
  deleteReview,
  getMyReview,
  getReviews,
  updateReview,
} from "../../api-clients/iwannacamp-api";
import useIdToken from "../../hooks/useIdToken";
import { createLogger } from "../../utils/logger";
import { openLoginModal } from "../auth/useOpenLoginModal";
import ReviewForm from "./ReviewForm";
import { Review, ReviewList } from "./ReviewList";
import ReviewSummary from "./ReviewSummary";

const logger = createLogger("ReviewsSection");

interface ReviewsSectionProps {
  /** Campground SQID */
  campgroundSqid: string;
  /** Average rating */
  averageRating?: number;
  /** Total review count */
  reviewCount?: number;
}

/**
 * Extract error message from API response error object.
 * Handles various error formats from the API client.
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === "object" && error !== null) {
    // Handle FastAPI error format: { detail: string | object | array }
    if ("detail" in error) {
      const detail = (error as any).detail;
      if (typeof detail === "string") {
        return detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Handle Pydantic validation errors (422): [{ loc: [...], msg: "...", type: "..." }]
        const firstError = detail[0];
        if (
          firstError &&
          typeof firstError === "object" &&
          "msg" in firstError
        ) {
          return String(firstError.msg);
        }
        return JSON.stringify(detail);
      } else if (
        typeof detail === "object" &&
        detail !== null &&
        "message" in detail
      ) {
        return String(detail.message);
      } else {
        return JSON.stringify(detail);
      }
    } else if ("message" in error) {
      return String((error as any).message);
    }
  } else if (typeof error === "string") {
    return error;
  }
  return fallback;
}

export const ReviewsSection: FunctionComponent<ReviewsSectionProps> = ({
  campgroundSqid,
  averageRating = 0,
  reviewCount = 0,
}) => {
  const auth = useAuth();
  const idToken = useIdToken();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Local state for rating and count (initialized from props, updated from API)
  const [localAverageRating, setLocalAverageRating] = useState(averageRating);
  const [localReviewCount, setLocalReviewCount] = useState(reviewCount);

  const pageSize = 10;

  // Fetch reviews when page changes (skip if review count is 0)
  useEffect(() => {
    if (reviewCount > 0) {
      loadReviews();
    }
  }, [page, campgroundSqid]);

  // Fetch user's review when authenticated
  useEffect(() => {
    if (idToken) {
      loadMyReview();
    }
  }, [idToken, campgroundSqid]);

  const loadReviews = async () => {
    // Prevent duplicate requests while one is already in-flight
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await getReviews({
        path: { campground_sqid: campgroundSqid },
        query: { page, page_size: pageSize },
      });

      if (response.data) {
        setReviews(response.data.reviews);
        setTotalPages(Math.ceil(response.data.total / pageSize));
        // Update local rating and count from API response
        setLocalReviewCount(response.data.total);
        setLocalAverageRating(response.data.average_rating);
      }
    } catch (err) {
      logger.error("Error loading reviews:", err);
      setError("Failed to load reviews");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMyReview = async () => {
    if (!idToken) return;

    try {
      const response = await getMyReview({
        path: { campground_sqid: campgroundSqid },
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.data) {
        setMyReview(response.data);
      } else {
        // No review found - clear the state
        setMyReview(null);
      }
    } catch (err) {
      logger.error("Error loading my review:", err);
      // On error (like 404), also clear the review
      setMyReview(null);
    }
  };

  const handleSubmitReview = async (
    rating: number,
    reviewText: string,
    turnstileToken: string
  ) => {
    if (!idToken) {
      openLoginModal();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let response;
      if (editingReview) {
        // Update existing review
        response = await updateReview({
          path: {
            campground_sqid: campgroundSqid,
            review_id: editingReview.id,
          },
          body: {
            rating,
            review_text: reviewText,
            turnstile_token: turnstileToken,
          },
          headers: { Authorization: `Bearer ${idToken}` },
        });
      } else {
        // Create new review
        response = await createReview({
          path: { campground_sqid: campgroundSqid },
          body: {
            rating,
            review_text: reviewText,
            turnstile_token: turnstileToken,
          },
          headers: { Authorization: `Bearer ${idToken}` },
        });
      }

      // Check if the API returned an error
      if (response.error) {
        setError(
          extractErrorMessage(response.error, "Failed to submit review")
        );
        return;
      }

      // Reload reviews and user's review
      await Promise.all([loadReviews(), loadMyReview()]);

      // Reset form
      setShowForm(false);
      setEditingReview(null);
    } catch (err: any) {
      logger.error("Error submitting review:", err);
      setError(err.message || "Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditReview = (review: Review) => {
    setEditingReview(review);
    setShowForm(true);
  };

  const handleDeleteReview = async (review: Review) => {
    if (!idToken) return;

    if (!confirm("Are you sure you want to delete your review?")) {
      return;
    }

    try {
      const response = await deleteReview({
        path: {
          campground_sqid: campgroundSqid,
          review_id: review.id,
        },
        headers: { Authorization: `Bearer ${idToken}` },
      });

      // Check if the API returned an error
      if (response.error) {
        setError(
          extractErrorMessage(response.error, "Failed to delete review")
        );
        return;
      }

      // Reload reviews and user's review
      await Promise.all([loadReviews(), loadMyReview()]);
    } catch (err) {
      logger.error("Error deleting review:", err);
      setError("Failed to delete review");
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingReview(null);
  };

  return (
    <Box>
      <Title order={3}>Reviews</Title>

      <ReviewSummary
        averageRating={localAverageRating}
        reviewCount={localReviewCount}
      />

      <Divider my="md" />

      {/* Show "Write a review" button if user hasn't reviewed yet */}
      {auth.currentUser && !myReview && !showForm && (
        <Button onClick={() => setShowForm(true)} mb="md">
          Write a Review
        </Button>
      )}

      {/* Show login prompt if not authenticated */}
      {!auth.currentUser && (
        <Button onClick={() => openLoginModal()} mb="md">
          Log in to Write a Review
        </Button>
      )}

      {/* Review form */}
      <Collapse expanded={showForm}>
        <Box mb="md">
          {error && (
            <Alert color="red" mb="md" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <ReviewForm
            initialRating={editingReview?.rating || 0}
            initialReviewText={editingReview?.review_text || ""}
            onSubmit={handleSubmitReview}
            onCancel={handleCancelEdit}
            submitText={editingReview ? "Update Review" : "Submit Review"}
            isSubmitting={isSubmitting}
          />
        </Box>
      </Collapse>

      {/* Reviews list */}
      <Stack gap="md">
        <ReviewList
          reviews={reviews}
          currentUserId={auth.currentUser?.uid}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onEdit={handleEditReview}
          onDelete={handleDeleteReview}
          isLoading={isLoading}
        />
      </Stack>
    </Box>
  );
};

export default ReviewsSection;
